import { InsightCondition } from '@api/api/insights';
import { Button } from '@design/components/button/Button';
import { Chip } from '@design/components/chip/Chip';
import { DropdownMenu } from '@design/components/dropdown-menu/DropdownMenu';
import { Option } from '@terminal/components/filter/Option';
import { WATCHLIST_ID, WORKING_LIST_ID } from '@terminal/config/constants';
import { useCompanyCompetitorWithThemes } from '@terminal/react-query-hooks/knowledge-graph/useKnowledgeGraph';
import { useWatchlist } from '@terminal/stores/use-watchlist/useWatchlist';
import { SelectedAssetItem } from '@terminal/views/dashboard/calendar-widget/calendar-event-drawer/drawer-content/run-scenario/RunScenario';
import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useScenarioFromInsights } from '../../hooks/useScenarioFromInsights';
import styles from './ScenarioTriggerSection.module.scss';

interface ScenarioTriggerSectionProps {
  entityName: string;
  entityTag: string;
  conditions: InsightCondition[];
}

export const ScenarioTriggerSection = ({
  entityName,
  entityTag,
  conditions,
}: ScenarioTriggerSectionProps) => {
  const { t } = useTranslation(['insights']);

  const lists = useWatchlist(state => state.lists);
  const [selectedAssetsItem, setSelectedAssetsItem] =
    useState<SelectedAssetItem>();

  const { onRunScenario } = useScenarioFromInsights({
    entityTag,
    conditions,
  });

  const { data } = useCompanyCompetitorWithThemes({
    entityTag,
  });

  const onOptionClick = (selectedAssetItem: SelectedAssetItem) => {
    setSelectedAssetsItem(selectedAssetItem);
  };

  const pillOptionsWithHeader = {
    ...(data
      ? {
          Connections: [
            {
              key: WORKING_LIST_ID,
              name: t('insights:runScenarioSection.peers'),
              value: { value: WORKING_LIST_ID },
            },
          ],
        }
      : {}),
    ...(lists.length > 0
      ? {
          Watchlist: lists.map(list => ({
            key: WATCHLIST_ID,
            name: list.name,
            value: { value: list.id },
          })),
        }
      : {}),
  };

  const hasOptions = Object.keys(pillOptionsWithHeader).length > 0;

  if (!hasOptions) {
    return null;
  }

  const firstOption = Object.values(pillOptionsWithHeader)[0][0];
  const selectedWatchlistName = lists.find(
    list => list.id === selectedAssetsItem?.id
  )?.name;
  return (
    <div className={styles.root} data-clipboard-snapshot>
      <div className={styles.row}>
        <div className={styles.flexColumn}>
          <div className={styles.titleLabel}>
            {t('insights:runScenarioSection.title', {
              name: entityName,
            })}
          </div>
          <div>
            <DropdownMenu
              triggerNode={
                <Chip
                  className={styles.styledChip}
                  label={selectedWatchlistName || firstOption.name}
                  rightIcon="ChevronLightDown"
                  rightIconSize={12}
                  type="input"
                />
              }
            >
              <div className={styles.scrollableList}>
                {Object.entries(pillOptionsWithHeader).map(
                  ([header, options], index) => {
                    const isLast =
                      index === Object.keys(pillOptionsWithHeader).length - 1;
                    return (
                      <Fragment key={header}>
                        <div>
                          <div className={styles.customHeaderLabel}>
                            {header}
                          </div>
                          {options.map(option => (
                            <Option
                              key={option.name}
                              option={option}
                              handleClick={() =>
                                onOptionClick({
                                  assetFilterValueKey:
                                    option.key === WORKING_LIST_ID
                                      ? WORKING_LIST_ID
                                      : WATCHLIST_ID,
                                  id: option.value.value,
                                })
                              }
                            />
                          ))}
                        </div>
                        {!isLast && <div className={styles.separator} />}
                      </Fragment>
                    );
                  }
                )}
              </div>
            </DropdownMenu>
          </div>
        </div>
        <Button
          className={styles.styledButton}
          variant="primary"
          label={t('insights:runScenarioSection.ctaButton')}
          onClick={() =>
            onRunScenario(
              selectedAssetsItem ?? {
                assetFilterValueKey: firstOption.key,
                id: firstOption.value.value,
              }
            )
          }
        />
      </div>
    </div>
  );
};
